import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./GoHighLevel.module.css";

interface Props {
  schedule: string;
  timezone: string;
  onChange: (schedule: string, timezone: string) => void;
}

type Frequency = "daily" | "weekly" | "monthly";

const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_OPTIONS = [1, 2, 3, 4, 5, 6, 0];

const detectedTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const getTimezones = (extra?: string): string[] => {
  let zones: string[] = [];
  try {
    zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    zones = [];
  }
  if (!zones.length) {
    zones = ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];
  }
  if (!zones.includes("UTC")) zones = ["UTC", ...zones];
  if (extra && !zones.includes(extra)) zones = [extra, ...zones];
  return zones;
};

const ordinal = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const pad = (n: number) => String(n).padStart(2, "0");

const expandCronField = (field: string, min: number, max: number): number[] => {
  const out = new Set<number>();
  for (const part of String(field).split(",")) {
    const [range, stepStr] = part.split("/");
    const step = stepStr ? parseInt(stepStr, 10) : 1;
    let lo: number, hi: number;
    if (range === "*") { lo = min; hi = max; }
    else if (range.includes("-")) { const [a, b] = range.split("-"); lo = parseInt(a, 10); hi = parseInt(b, 10); }
    else { lo = hi = parseInt(range, 10); }
    if (isNaN(lo) || isNaN(hi) || isNaN(step) || step < 1) continue;
    for (let n = lo; n <= hi; n += step) out.add(n);
  }
  return [...out];
};

interface ScheduleState {
  frequency: Frequency;
  time: string;
  weekdays: number[];
  monthDay: number;
}

const parseCron = (cron: string): ScheduleState => {
  const f = (cron || "0 2 * * *").trim().split(/\s+/);
  const min = parseInt(f[0], 10) || 0;
  const hour = parseInt(f[1], 10) || 0;
  const dom = f[2];
  const dow = f[4];
  const time = `${pad(hour)}:${pad(min)}`;

  if (dow && dow !== "*") {
    const weekdays = [...new Set(expandCronField(dow, 0, 7).map((n) => n % 7))];
    return { frequency: "weekly", time, weekdays: weekdays.length ? weekdays : [1], monthDay: 1 };
  }
  if (dom && dom !== "*") {
    let d = parseInt(dom, 10) || 1;
    if (d > 28) d = 28;
    return { frequency: "monthly", time, weekdays: [1], monthDay: d };
  }
  return { frequency: "daily", time, weekdays: [1], monthDay: 1 };
};

const buildCron = (s: ScheduleState): string => {
  const [h, m] = (s.time || "02:00").split(":").map((n) => parseInt(n, 10));
  const hour = isNaN(h) ? 2 : h;
  const min = isNaN(m) ? 0 : m;

  if (s.frequency === "weekly") {
    const days = (s.weekdays.length ? s.weekdays : [1]).slice().sort((a, b) => a - b);
    return `${min} ${hour} * * ${days.join(",")}`;
  }
  if (s.frequency === "monthly") {
    return `${min} ${hour} ${s.monthDay || 1} * *`;
  }
  return `${min} ${hour} * * *`;
};

const BulkScrubScheduleBuilder: React.FC<Props> = ({ schedule, timezone, onChange }) => {
  const [state, setState] = useState<ScheduleState>(() => parseCron(schedule));
  const [tz, setTz] = useState<string>(timezone || detectedTimezone());
  const timezones = useMemo(() => getTimezones(tz), [tz]);
  const firstRun = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    onChangeRef.current(buildCron(state), tz);
  }, [state, tz]);

  const update = (patch: Partial<ScheduleState>) => setState((prev) => ({ ...prev, ...patch }));

  const toggleWeekday = (day: number, checked: boolean) =>
    setState((prev) => ({
      ...prev,
      weekdays: checked ? [...prev.weekdays, day] : prev.weekdays.filter((d) => d !== day),
    }));

  const summary = useMemo(() => {
    const [h, m] = (state.time || "02:00").split(":").map((n) => parseInt(n, 10));
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    const time12 = `${h12}:${pad(m)} ${ampm}`;
    let when = `every day at ${time12}`;
    if (state.frequency === "weekly") {
      const days = state.weekdays.slice().sort((a, b) => a - b).map((d) => DOW_NAMES[d]);
      when = days.length ? `every ${days.join(", ")} at ${time12}` : `weekly at ${time12} — select at least one day`;
    } else if (state.frequency === "monthly") {
      when = `on the ${ordinal(state.monthDay || 1)} of every month at ${time12}`;
    }
    return `Runs ${when} (${tz.replace(/_/g, " ")}).`;
  }, [state, tz]);

  return (
    <div>
      <div className="row g-3">
        <div className="col-sm-6">
          <label className={styles.formLabel}>Frequency</label>
          <select
            className="form-select form-select-sm"
            value={state.frequency}
            onChange={(e) => update({ frequency: e.target.value as Frequency })}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div className="col-sm-6">
          <label className={styles.formLabel}>At time</label>
          <input
            type="time"
            className="form-control form-control-sm"
            value={state.time}
            onChange={(e) => update({ time: e.target.value })}
          />
        </div>
      </div>

      {state.frequency === "weekly" && (
        <div className={styles.formGroup} style={{ marginTop: "0.75rem" }}>
          <label className={styles.formLabel}>On these days</label>
          <div className="d-flex flex-wrap gap-3">
            {WEEKDAY_OPTIONS.map((day) => (
              <label key={day} className="form-check-label d-inline-flex align-items-center gap-1">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={state.weekdays.includes(day)}
                  onChange={(e) => toggleWeekday(day, e.target.checked)}
                />
                {DOW_NAMES[day]}
              </label>
            ))}
          </div>
        </div>
      )}

      {state.frequency === "monthly" && (
        <div className={styles.formGroup} style={{ marginTop: "0.75rem" }}>
          <label className={styles.formLabel}>On day of month</label>
          <select
            className="form-select form-select-sm"
            value={state.monthDay}
            onChange={(e) => update({ monthDay: parseInt(e.target.value, 10) })}
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <div className={styles.formHint}>Choose a day between 1 and 28 so it runs every month.</div>
        </div>
      )}

      <div className={styles.formGroup} style={{ marginTop: "0.75rem" }}>
        <label className={styles.formLabel}>Timezone</label>
        <select
          className="form-select form-select-sm"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
        >
          {timezones.map((z) => (
            <option key={z} value={z}>{z.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      <div className={styles.formHint} style={{ marginTop: "0.5rem" }}>{summary}</div>
    </div>
  );
};

export default BulkScrubScheduleBuilder;
